import type React from 'react';
import { useContext, useState } from 'react';
import {
  Button,
  ButtonGroup,
  FieldSet,
  Form,
  Heading,
  Legend,
  Modal,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import { AppContext } from '../../../contexts/AppContextProvider';
import { logAppError } from '../../../utils/errorUtils';
import stringUtils from '../../../utils/stringUtils';

import type { AttachmentsForMessage } from '../../MessageAttachments';
import { TooltipWrapper } from '@zg-rentals/satellite-components';
import AttachmentCardSelector from './components/AttachmentCardSelector';
import { MessageList_Rh_GetConversationDocument } from '../../../types/GraphqlTypes';
import { useSatelliteWebConversationHandler_deleteAttachments } from '@zg-rentals/ts-satellite-client-react-query';
import { useRefetchQueriesWithRetry } from '../../../hooks/useRefetchQueriesWithRetry';

type Props = {
  attachments: AttachmentsForMessage;
  conversationId: string;
  messageId: string;
  onClose(): void;
};

export type HandleSelectAttachmentToDelete = (zDocId: string, isChecked: boolean) => void;

export default function DeleteAttachments({ attachments, conversationId, messageId, onClose }: Props) {
  const { enqueueToast } = useToast();
  const { isImpersonator } = useContext(AppContext);
  const { mutateAsync: deleteAttachments } = useSatelliteWebConversationHandler_deleteAttachments();
  const refetchQueries = useRefetchQueriesWithRetry({
    errorContext: 'component.Modal.DeleteAttachments.handleDeleteAttachments',
  });

  const [checkedAttachments, setCheckedAttachments] = useState<Array<string>>([]);
  const isDeleteButtonDisabled = checkedAttachments.length === 0 || isImpersonator;
  const legendText = `Select ${stringUtils.pluralize('attachment', attachments.length)} to delete`;

  const handleClose = () => onClose();

  const handleSelectAttachmentToDelete: HandleSelectAttachmentToDelete = (zDocId, isChecked) => {
    if (isChecked) {
      setCheckedAttachments([...checkedAttachments, zDocId]);
    } else {
      const filteredAttachments = checkedAttachments.filter((checkedAttachment) => checkedAttachment !== zDocId);
      setCheckedAttachments(filteredAttachments);
    }
  };

  const handleDeleteAttachments = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    try {
      const attachmentsToBeDeleted = checkedAttachments.map((zdocId) => {
        return {
          messageId,
          zdocId,
        };
      });

      await deleteAttachments({
        postData: {
          conversationId,
          attachmentsToBeDeleted,
        },
      });

      enqueueToast(<Toast tone="success">Deleted</Toast>);
      onClose();

      // Refetch conversation with retry - don't block on this
      refetchQueries([
        {
          query: MessageList_Rh_GetConversationDocument,
          variables: { conversationId },
          name: 'MessageList',
        },
      ]);
    } catch (error) {
      enqueueToast(
        <Toast tone="critical">Sorry, there was an error deleting one or more attachments. Please try again.</Toast>,
      );
      onClose();

      logAppError({
        context: { errorLocation: 'component.Modal.DeleteAttachments.handleDeleteAttachments' },
        message: error.message,
        error,
      });
    }
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && handleClose()}
      size={{
        base: 'fullScreen',
        md: 'md',
      }}
      header={
        <Heading textStyle="heading-xs" level={2}>
          Delete?
        </Heading>
      }
      body={
        <Form onSubmit={(e: React.FormEvent<HTMLFormElement>) => e.preventDefault()}>
          <FieldSet legend={<Legend size="lg">{legendText}</Legend>} css={{ minWidth: '100%' }}>
            {attachments.map(({ fileName, fileType, isAttachmentExpired, isAttachmentDeleted, previewUrl, zDocId }) => {
              if (isAttachmentExpired || isAttachmentDeleted) {
                return;
              }

              const key = `${fileName}-${zDocId}`;

              return (
                <AttachmentCardSelector
                  key={key}
                  fileName={fileName || ''}
                  fileType={fileType || ''}
                  imageSrc={previewUrl || ''}
                  onSelect={handleSelectAttachmentToDelete}
                  zDocId={zDocId || ''}
                />
              );
            })}
          </FieldSet>
        </Form>
      }
      footer={
        <ButtonGroup aria-label="form actions">
          <TextButton onClick={handleClose}>Cancel</TextButton>
          <TooltipWrapper wrapInTooltip={isImpersonator}>
            <Button
              disabled={isDeleteButtonDisabled}
              emphasis="filled"
              onClick={handleDeleteAttachments}
              tone="brand"
              type="submit"
            >
              Delete
            </Button>
          </TooltipWrapper>
        </ButtonGroup>
      }
    />
  );
}
